/**
 * useAppSettings — Reads app_settings from DB with TanStack Query.
 * Falls back gracefully when rows are missing.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type SettingsMap = Record<string, unknown>;

const QUERY_KEY = 'app-settings';

/** Fetch specific keys (or all) from app_settings */
async function fetchAppSettings(keys?: string[]): Promise<SettingsMap> {
  let query = supabase.from('app_settings').select('key, value');
  if (keys && keys.length > 0) {
    query = query.in('key', keys);
  }
  const { data, error } = await query;
  if (error) {
    console.warn('[useAppSettings] fetch failed, using fallbacks:', error.message);
    return {};
  }
  const map: SettingsMap = {};
  for (const row of data ?? []) {
    map[row.key] = row.value;
  }
  return map;
}

/**
 * Hook: subscribe to app_settings with fallback defaults.
 * @param keys - settings keys to fetch (undefined = all)
 * @param fallback - default values when keys are missing from DB
 */
export function useAppSettings(keys?: string[], fallback: SettingsMap = {}) {
  const query = useQuery({
    queryKey: [QUERY_KEY, keys],
    queryFn: () => fetchAppSettings(keys),
    staleTime: 5 * 60_000, // 5 min cache
    retry: 1,
  });

  /** Merged: DB values override fallback */
  const settings: SettingsMap = { ...fallback, ...(query.data ?? {}) };

  /** Convenience getter with type-safe fallback */
  function get<T = unknown>(key: string, defaultVal?: T): T {
    return (settings[key] as T) ?? (defaultVal as T);
  }

  return { settings, get, isLoading: query.isLoading, error: query.error };
}
